'use client';

import { useEffect } from 'react';

import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';

const MAX_DEPTH = 10;
const GTM_EVENT_SET = new Set(Object.values(GTM_EVENT_TYPE));

const isValidGtmEventType = (
  eventType: string,
): eventType is GTM_EVENT_TYPE => {
  return GTM_EVENT_SET.has(eventType as GTM_EVENT_TYPE);
};

const isHTMLElement = (
  target: EventTarget | null,
): target is HTMLElement | SVGElement => {
  return target instanceof HTMLElement || target instanceof SVGElement;
};

/**
 * GTM 이벤트를 자동으로 추적하는 전역 이벤트 리스너 훅
 * data-event 속성이 GTM_EVENT_TYPE에 해당하는 엘리먼트 클릭 시 자동으로 GTM 이벤트를 전송합니다.
 */
export const useGtmEventClickListener = () => {
  useEffect(() => {
    const handleElementClick = (event: Event) => {
      const target = event.target;
      if (!isHTMLElement(target)) return;

      let element: HTMLElement | SVGElement | null = target;
      let depth = 0;

      while (element && element !== document.body && depth < MAX_DEPTH) {
        // HTMLElement인 경우에만 dataset에 접근 (SVGElement는 dataset이 없음)
        const dataset =
          element instanceof HTMLElement ? element.dataset : undefined;

        const eventType = dataset?.event;

        if (eventType && isValidGtmEventType(eventType)) {
          const params = { ...dataset };
          delete params.event;

          trackGtmEvent(eventType, params);

          return;
        }

        element = element.parentElement;
        depth++;
      }
    };

    document.addEventListener('click', handleElementClick);

    return () => {
      document.removeEventListener('click', handleElementClick);
    };
  }, []);
};
